import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'
import MainContainer from '@/components/generals/main-container'
import EditorialGrid from '@/components/generals/editorial-grid'
import DisplayHeading from '@/components/typography/display-heading'

interface PageHeaderProps {
    eyebrow: ReactNode
    title: ReactNode
    intro?: ReactNode
    aside?: ReactNode
    className?: string
    tone?: 'paper' | 'ink'
}

/** Opening block for section index pages: label, large title, intro and an optional aside. */
const PageHeader: FC<PageHeaderProps> = ({ eyebrow, title, intro, aside, className, tone = 'paper' }): ReactNode => {
    return (
        <header className={cn(tone === 'ink' && 'tone-ink', className)}>
            <MainContainer className='pt-12 pb-14 md:pt-20 md:pb-20'>
                <p className='flex items-center gap-2.5 font-mono text-label uppercase text-muted-foreground'>
                    <span aria-hidden='true' className='size-1.5 bg-brand' />
                    {eyebrow}
                </p>

                <EditorialGrid className='mt-6 gap-y-8 md:mt-10'>
                    <DisplayHeading
                        as='h1'
                        size='headline'
                        className='col-span-4 animate-in fade-in slide-in-from-bottom-2 duration-700 ease-out md:col-span-9 lg:col-span-8'
                    >
                        {title}
                    </DisplayHeading>

                    {aside && (
                        <div className='col-span-4 md:col-span-3 md:self-end lg:col-span-3 lg:col-start-10'>
                            {aside}
                        </div>
                    )}
                </EditorialGrid>

                {intro && (
                    <div className='mt-8 max-w-[60ch] text-lede text-muted-foreground md:mt-10'>
                        {intro}
                    </div>
                )}
            </MainContainer>
        </header>
    )
}

export default PageHeader
